const GROQ_API_KEY = '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';
const GROQ_API_URL = 'https://api.groq.com/openai/v1/chat/completions';

export interface SudokuHint {
  row: number;
  col: number;
  value: number;
  explanation: string;
}

export async function getAIHint(
  currentState: number[][], 
  solution: number[][],
  language: string = 'ru'
): Promise<SudokuHint> {
  // Find one cell that is empty in currentState but has a value in solution
  let hintCell: {row: number; col: number; value: number} | null = null;
  
  for (let row = 0; row < 9; row++) {
    for (let col = 0; col < 9; col++) {
      if (currentState[row][col] === 0 && solution[row][col] !== 0) {
        hintCell = { row, col, value: solution[row][col] };
        break;
      }
    }
    if (hintCell) break;
  }

  if (!hintCell) {
    throw new Error('No empty cells found in current state');
  }

  // Format current board state for AI (simple 9x9 grid, 0 = empty)
  const boardStr = currentState
    .map((row) => row.map((cell) => cell === 0 ? '_' : cell).join(' '))
    .join('\n');

  // Determine language for prompt
  const isEnglish = language === 'en';

  const prompt = isEnglish
    ? `Hello! You are an experienced Sudoku assistant. I need your help with one cell.

Here is the current state of the game board (9 rows, each row - 9 cells, _ means an empty cell, numbers - already filled values):

${boardStr}

I need to understand what number should be in one of the empty cells. The correct answer is the number ${hintCell.value}.

Please explain to me in simple and understandable language why exactly this number should be here. Use different formulations in your explanations to make them interesting and varied. For example, you can say "this is the only possible number", "all other numbers are already used", "this logically follows from the rules", "can be determined by Sudoku rules", "other numbers are already present in this row/column/block", and so on.

The explanation should be:
- Polite and friendly
- Brief (1-2 sentences)
- Understandable for an ordinary person
- Justified by Sudoku rules
- Varied (don't repeat the same phrases every time)

Answer ONLY in JSON format (without markdown, without additional text):
{"row": ${hintCell.row}, "col": ${hintCell.col}, "value": ${hintCell.value}, "explanation": "<your polite and understandable explanation in English>"}`
    : `Привет! Ты опытный помощник по Судоку. Мне нужна твоя помощь с одной ячейкой.

Вот текущее состояние игрового поля (9 строк, каждая строка - 9 ячеек, _ означает пустую ячейку, цифры - уже заполненные значения):

${boardStr}

Мне нужно понять, какое число должно быть в одной из пустых ячеек. Правильный ответ - это число ${hintCell.value}.

Пожалуйста, объясни мне простым и понятным языком, почему именно это число должно быть здесь. Используй разные формулировки в своих объяснениях, чтобы они были интересными и разнообразными. Например, можно сказать "это единственное возможное число", "все остальные числа уже использованы", "логически это следует из правил", "можно определить по правилам Судоку", "остальные числа уже присутствуют в этой строке/столбце/блоке", и так далее.

Объяснение должно быть:
- Вежливым и дружелюбным
- Кратким (1-2 предложения)
- Понятным для обычного человека
- Обоснованным правилами Судоку
- Разнообразным (не повторяй одни и те же фразы каждый раз)

Отвечай ТОЛЬКО в формате JSON (без markdown, без дополнительного текста):
{"row": ${hintCell.row}, "col": ${hintCell.col}, "value": ${hintCell.value}, "explanation": "<твое вежливое и понятное объяснение на русском языке>"}`;

  try {
    const response = await fetch(GROQ_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${GROQ_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'llama-3.3-70b-versatile',
        messages: [
          {
            role: 'system',
            content: 'You are a Sudoku expert assistant. Always respond with valid JSON only.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7, // Higher temperature for more diverse explanations
        max_tokens: 300,
      }),
    });

    if (!response.ok) {
      throw new Error(`Groq API error: ${response.status}`);
    }

    const data = await response.json();
    const content = data.choices[0]?.message?.content?.trim() || '';
    
    // Clean up markdown code blocks if present
    let jsonStr = content;
    if (jsonStr.startsWith('```json')) {
      jsonStr = jsonStr.replace(/```json\n?/g, '').replace(/```\n?/g, '');
    } else if (jsonStr.startsWith('```')) {
      jsonStr = jsonStr.replace(/```\n?/g, '');
    }
    
    const hint = JSON.parse(jsonStr);
    
    // Validate hint
    if (
      typeof hint.row !== 'number' || hint.row < 0 || hint.row > 8 ||
      typeof hint.col !== 'number' || hint.col < 0 || hint.col > 8 ||
      typeof hint.value !== 'number' || hint.value < 1 || hint.value > 9 ||
      typeof hint.explanation !== 'string'
    ) {
      throw new Error('Invalid hint format');
    }

    // Validate that the cell matches the expected hint cell
    if (hint.row !== hintCell.row || hint.col !== hintCell.col || hint.value !== hintCell.value) {
      // Use the correct values from hintCell
      return {
        row: hintCell.row,
        col: hintCell.col,
        value: hintCell.value,
        explanation: hint.explanation || (language === 'en' ? `This cell should contain ${hintCell.value}.` : `В этой ячейке должно быть ${hintCell.value}.`)
      };
    }

    return hint;
  } catch (error) {
    console.error('Groq API error:', error);
    throw error;
  }
}


